import { RowData } from '../App';
import { Block } from '../classes/Block';
import type { VideoSettings, RenderFormat, VideoMode, SecondsPerCard } from '../components/VideoSettings';
import { rebuildCardGroupsFromIndices } from '../utils/cardGroups';

export interface DataColumn {
  field: string;
  headerName: string;
  flex?: number;
  sortable?: boolean;
  filter?: boolean;
  resizable?: boolean;
}

export type LoaderOnFileUpload = (
  data: RowData[],
  columns: DataColumn[],
  sheetName: string,
  blocks?: Block[],
  videoSettings?: VideoSettings,
  cardGroups?: import('../utils/cardGroups').CardGroup[]
) => void;

export const DEFAULT_COLUMNS: DataColumn[] = [
  { field: 'col1', headerName: 'Label' },
  { field: 'col2', headerName: 'Value' }
];

export const getBooleanValue = (value: unknown, defaultValue: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
  if (typeof value === 'number') return value === 1;
  return defaultValue;
};

export const getNumericValue = (value: unknown, defaultValue: number): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && !isNaN(Number(value))) return Number(value);
  return defaultValue;
};

export type SbsBlocksOption = 'normal' | 'left' | 'right';

export const getSbsBlocksValue = (
  value: unknown,
  defaultValue: SbsBlocksOption
): SbsBlocksOption => {
  return Block.normalizeSbsBlocks(value, defaultValue);
};

const parseSecondsPerCard = (value: unknown): SecondsPerCard => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsedValue: unknown = typeof value === 'string'
    ? (() => {
      const trimmed: string = value.trim();
      if (!trimmed) {
        return null;
      }
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    })()
    : value;

  if (typeof parsedValue === 'number') {
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  if (Array.isArray(parsedValue)) {
    const normalized: number[] = parsedValue
      .map((entry: unknown) => Number(entry))
      .filter((entry: number) => Number.isFinite(entry) && entry > 0);
    return normalized.length > 0 ? normalized : null;
  }

  return null;
};

export const parseVideoSettings = (firstRow: unknown[]): VideoSettings | undefined => {
  if (!Array.isArray(firstRow) || firstRow.length < 2 || firstRow[0] !== 'video_settings') {
    return undefined;
  }

  const settings: Record<string, unknown> = {};
  for (let i = 1; i < firstRow.length - 1; i += 2) {
    const key = firstRow[i];
    const value = firstRow[i + 1];
    if (key) {
      settings[String(key)] = value;
    }
  }

  if (Object.keys(settings).length === 0) {
    return undefined;
  }

  let fontsMap: Record<string, string> | undefined;
  const fontsRaw = settings.fonts;
  if (typeof fontsRaw === 'string') {
    try {
      fontsMap = JSON.parse(fontsRaw);
    } catch {
      /* ignore */
    }
  } else if (fontsRaw && typeof fontsRaw === 'object') {
    fontsMap = fontsRaw as Record<string, string>;
  }

  let logosMap: Record<string, { url: string; isEnabled: boolean }> | undefined;
  const logosRaw = (settings as any).logos;
  if (typeof logosRaw === 'string' && logosRaw.trim()) {
    try {
      const parsed = JSON.parse(logosRaw);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        logosMap = parsed;
      }
    } catch {
      /* ignore */
    }
  } else if (logosRaw && typeof logosRaw === 'object' && Object.keys(logosRaw).length > 0) {
    logosMap = logosRaw as Record<string, { url: string; isEnabled: boolean }>;
  }

  return {
    background_color: (settings.background_color as string) || '#000000',
    fps_num: parseInt(String(settings.fps_num)) || 30,
    resolution: (settings.resolution as string) || 'HD',
    transparent: getBooleanValue(settings.transparent, false),
    video_time: parseFloat(String(settings.video_time)) || 0,
    aspect_ratio: (settings.aspect_ratio as string) || '16:9',
    video_format: ((settings.video_format as string) || (settings.format as string) || 'H264') as RenderFormat,
    fonts: fontsMap || {},
    logos: logosMap || {},
    mode: (settings.mode as VideoMode) || "roller",
    seconds_per_card: parseSecondsPerCard(settings.seconds_per_card),
  };
};

export interface ExtractedData {
  allSheetDataRows: RowData[];
  blocks: Block[];
  videoSettings?: VideoSettings;
  columns: DataColumn[];
  cardGroups?: import('../utils/cardGroups').CardGroup[];
}

export const extractDataFromRows = (jsonData: unknown[]): ExtractedData => {
  if (!Array.isArray(jsonData) || jsonData.length === 0) {
    throw new Error('No data rows provided');
  }

  const columns: DataColumn[] = DEFAULT_COLUMNS;

  const allSheetDataRows: RowData[] = [];
  const allProcessedBlocks: Block[] = [];
  const blockCardIndices = new Map<number, number>();
  let currentBlock: Block | null = null;

  let videoSettings: VideoSettings | undefined;
  if (jsonData.length > 0) {
    videoSettings = parseVideoSettings(jsonData[0] as unknown[]);
  }

  for (let i = videoSettings ? 1 : 0; i < jsonData.length; i++) {
    const rowArray = jsonData[i] as unknown[];
    const dataRow: RowData = {};
    rowArray.forEach((value, index) => {
      dataRow[`col${index}`] = value as string | number;
    });
    allSheetDataRows.push(dataRow);

    const blockName = String((rowArray[0] as unknown) || '').trim();
    if (blockName) {
      const props: Record<string, unknown> = {};
      if (rowArray.length > 2) {
        for (let j = 1; j < rowArray.length - 1; j += 2) {
          const key = String(rowArray[j]);
          const value = rowArray[j + 1];
          if (key) {
            props[key] = value;
          }
        }
      }

      const blockIndex: number = allProcessedBlocks.length;
      const cardIndexRaw = props['card_index'];
      if (cardIndexRaw !== undefined && cardIndexRaw !== null) {
        const cardIndex = getNumericValue(cardIndexRaw, -1);
        if (cardIndex >= 0) {
          blockCardIndices.set(blockIndex, cardIndex);
        }
      }

      currentBlock = Block.create({
        name: blockName,
        shouldDisplayBlockName: getBooleanValue(props['show_block_name'], true),
        blockNameFont: String(props['block_name_font_style'] || 'Arial'),
        blockNameSize: getNumericValue(props['block_name_font_size'], 30),
        blockNameWeight: getNumericValue(props['block_name_font_weight'], 400),
        blockNameColor: String(props['block_name_color'] || '#000000'),
        numColumns: getNumericValue(props['column_num'], 1),
        labelValueGap: getNumericValue(props['label_value_gap'], 5),
        distanceBelowBlockName: getNumericValue(props['distance_below_block_name'], 10),
        marginColumn: getNumericValue(props['margin_column'], 45),
        edgeDistance: getNumericValue(props['edge_distance'], 0),
        distanceBetweenRows: getNumericValue(props['distance_between_rows'], 1),
        distanceFromPreviousBlock: getNumericValue(props['distance_from_previous'], 10),
        isSongBlock: getBooleanValue(props['song_block'], false),
        isSingleCell: getBooleanValue(props['single_cell'], false),
        isCreditsList: getBooleanValue(props['credits_list'], false),
        separator: String(props['credits_list_separator'] || '•'),
        isLogoBlock: getBooleanValue(props['logo_block'], false),
        sbsBlocks: getSbsBlocksValue(props['sbs_blocks'], 'normal'),
        sbsDistance: getNumericValue(props['sbs_distance'], 0),
        labelFontName: String(props['label_font_name'] || 'Arial'),
        labelFontSize: getNumericValue(props['label_font_size'], 11),
        labelFontWeight: getNumericValue(props['label_font_weight'], 400),
        labelAlign: String(props['label_align'] || 'right'),
        labelVerticalAlign: String(props['label_vertical_align'] || 'bottom'),
        labelFontColor: String(props['label_color'] || '#000000'),
        valueFontName: String(props['value_font_name'] || 'Arial'),
        valueFontSize: getNumericValue(props['value_font_size'], 11),
        valueFontWeight: getNumericValue(props['value_font_weight'], 400),
        valueAlign: String(props['value_align'] || 'left'),
        valueVerticalAlign: String(props['value_vertical_align'] || 'bottom'),
        valueFontColor: String(props['value_color'] || '#000000'),
        hasSeparateValueFormatting: getBooleanValue(props['has_separate_value_formatting'], false),
        isLocked: getBooleanValue(props['block_locked'], false),
        dataRows: []
      });
      allProcessedBlocks.push(currentBlock);
    } else if (currentBlock) {
      if (rowArray
        .slice(1)
        .some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
        currentBlock.dataRows.push(dataRow);
      }
      else {
        currentBlock.dataRows.push({ col1: '', col2: '' } as RowData);
      }
    }
  }

  for (const block of allProcessedBlocks) {
    if (block.dataRows.length === 0) {
      const emptyRow: RowData = {};
      for (const column of columns) {
        emptyRow[column.field] = '';
      }
      block.dataRows.push(emptyRow);
    }
  }

  const cardGroups =
    videoSettings?.mode === 'cards' && blockCardIndices.size > 0
      ? rebuildCardGroupsFromIndices(allProcessedBlocks, blockCardIndices)
      : undefined;

  return {
    allSheetDataRows,
    blocks: allProcessedBlocks,
    videoSettings,
    columns,
    cardGroups,
  };
};

